// W47b (2026-10-21): the approved "Review this gift" card (data-component
// "gift-review") on a real session. The facts come from the W47 read: the
// customer name from the HubSpot profile, delivery from the subscription's
// next charge on the contact, the gift price as "Price from the pricing
// engine" (never a typed number). "Add to next shipment" follows the
// approved rule, enabled only after the agreement checkbox AND the gate;
// the gate is closed on this deployment (no gift write route, W47
// actions.addToNextShipment.available false), so the button stays inert
// and says so.
import type { ReactNode } from 'react';
import { LIVE_WRITE_PAUSED_LABEL } from '../../_w2a_lower/liveWrite.js';
import { GIFT_PRICE_PLACEHOLDER, type GiftEligibilityView, type LiveGiftCatalogItem } from '../liveModel.js';

export interface LiveGiftReviewProps {
  view: GiftEligibilityView;
  selected: LiveGiftCatalogItem | null;
  customerName: string;
  firstName: string;
  size: string | null;
  agreed: boolean;
  onSizeChange: (size: string) => void;
  onAgreementChange: (agreed: boolean) => void;
}

export function deliverySummary(view: GiftEligibilityView): string {
  if (view.subscription.active === true) {
    return view.subscription.nextChargeAt ? `Next charge on file: ${view.subscription.nextChargeAt}` : 'Subscription active; next charge date not on file';
  }
  if (view.subscription.active === false) return 'No active subscription shipment on file';
  return 'Subscription not read';
}

export function LiveGiftReview({ view, selected, customerName, firstName, size, agreed, onSizeChange, onAgreementChange }: LiveGiftReviewProps): ReactNode {
  const sizes = selected?.sizes ?? null;
  const gateOpen = view.addToNextShipment.available;
  const canSubmit = agreed && gateOpen && selected !== null;
  const variantSummary = selected ? [selected.variant, sizes && size ? size : null].filter(Boolean).join(' · ') || selected.name : 'No gift selected';
  return (
    <section className="card" data-component="gift-review" data-testid="gifts-live-review">
      <div className="card-head">
        <h3>Review this gift</h3>
        <span className="small">Not submitted</span>
      </div>
      <div className="gift-review">
        <span className="gift-kind">SELECTED GIFT</span>
        <h2 id="gift-name">{selected?.name ?? 'Choose a gift'}</h2>
        <p className="selected-variant-label" id="gift-variant-label">{selected?.variant ?? 'Pick a catalog item to review it here.'}</p>
        {sizes && sizes.length > 0 ? (
          <div id="gift-size">
            <label htmlFor="shirt-size">Shirt size</label>
            <div className="sizes" id="shirt-size">
              {sizes.map(s => (
                <button key={s} type="button" className={s === size ? 'selected-size' : undefined} aria-pressed={s === size} onClick={() => onSizeChange(s)}>{s}</button>
              ))}
            </div>
          </div>
        ) : null}
        <div className="gift-facts">
          <div><span>Customer</span><strong>{customerName}</strong></div>
          <div><span>Delivery</span><strong id="gift-shipment" data-testid="gifts-delivery">{deliverySummary(view)}</strong></div>
          <div><span>Variant</span><strong id="gift-variant-summary">{variantSummary}</strong></div>
          <div><span>Gift price</span><strong data-testid="gifts-price">{GIFT_PRICE_PLACEHOLDER}</strong></div>
          <div><span>Shipping</span><strong id="shipping-summary">Not quoted on this deployment</strong></div>
          <div><span>Repeats</span><strong>No · One-time gift</strong></div>
        </div>
        <label className="gift-agreement">
          <input id="gift-agreed" type="checkbox" checked={agreed} onChange={e => onAgreementChange(e.target.checked)} />{' '}
          <span id="agreement-copy">{firstName} agreed to this gift and the selected shipment.</span>
        </label>
        <button className="blue-action" id="attach-gift" type="button" disabled={!canSubmit} aria-describedby="review-note" title={gateOpen ? undefined : LIVE_WRITE_PAUSED_LABEL} data-live-write-paused={gateOpen ? undefined : 'true'}>
          Add to next shipment
        </button>
        <p className="gift-review-note" id="review-note" data-testid="gifts-review-note">
          {gateOpen
            ? 'Confirm attachment before telling the customer it is on the order. Attached is not the same as shipped.'
            : agreed
              ? `${LIVE_WRITE_PAUSED_LABEL}: ${view.addToNextShipment.note ?? 'no gift write route exists on this deployment.'} Nothing is attached from this screen.`
              : `Confirm the customer agreed first. ${LIVE_WRITE_PAUSED_LABEL}: no gift write route exists on this deployment, so the button stays inert even after agreement.`}
        </p>
      </div>
    </section>
  );
}
